import React, { useEffect, useState } from 'react';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { CompetitionRubric } from '../components/competition/RubricScoreForm';
import { apiService } from '../services/api';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import '../components/Auth/RegisterModal.css';
import './ManageCompetitionModals.css';

interface Results {
  rubric?: CompetitionRubric;
  leaderboard: { username: string; score: number; evaluation_count: number; criterion_scores: { criterion_id: number; average_score: number }[] }[];
  evaluations: { id: number; judge: string; participant: string; submission_version: number; overall_score: number; feedback?: string;
    scores?: { criterion_id: number; score: number }[] }[];
  judges?: { judge: string; completed_evaluations: number; max_evaluations: number }[];
  total_evaluations?: number;
}

interface Props { isOpen: boolean; onClose: () => void; competitionId: number; mode: 'rubric' | 'single_score'; }

const ManageCompetitionScoringResultsModal: React.FC<Props> = ({ isOpen, onClose, competitionId, mode }) => {
  const [results, setResults] = useState<Results | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const load = async (more = false) => {
    setLoading(true); setError('');
    try {
      if (mode === 'rubric') {
        const response = await apiService.get(`/evaluations/rubric/${competitionId}/results`, { params: { limit: 1000, offset: more ? results?.evaluations.length || 0 : 0 } });
        setResults({ ...response.data, evaluations: more ? [...(results?.evaluations || []), ...response.data.evaluations] : response.data.evaluations });
      } else {
        const response = await apiService.get(`/evaluations/${competitionId}/manual_scores`, { params: { limit: 5000 } });
        setResults({ leaderboard: [], evaluations: response.data.map((item: any) => ({ ...item, participant: item.team_name || item.username, overall_score: item.score })) });
      }
    } catch (err: any) { setError(err?.response?.data?.detail || 'Could not load evaluation results.'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!isOpen) return;
    setResults(null); setQuery('');
    load();
    // The modal loads on opening; Refresh intentionally performs subsequent reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, competitionId, mode]);
  useEscapeToClose(onClose, { enabled: isOpen });

  if (!isOpen) return null;
  const criteria = results?.rubric?.criteria || [];
  const evaluations = results?.evaluations.filter((item) => `${item.judge} ${item.participant}`.toLowerCase().includes(query.toLowerCase())) || [];
  const maxScore = mode === 'rubric' ? 5 : 100;
  return <div className="register-modal-backdrop" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="register-modal scoring-results-modal" role="dialog" aria-modal="true" aria-label="Evaluation results">
      <div className="register-modal__header"><h2>Evaluation results</h2><Button onClick={onClose}>Close</Button></div>
      <div className="register-modal__form">
        {error && <p role="alert" className="register-modal__error">{error}</p>}
        {loading && <LoadingSpinner text="Loading evaluations..." />}
        <Button onClick={() => load()} disabled={loading}>Refresh results</Button>
        {results?.rubric && <p>{results.rubric.title} · {criteria.length} criteria · scores out of 5</p>}
        {mode === 'rubric' && <>
          <h3>Leaderboard</h3>
          <p>Overall scores average each judge’s weighted criterion scores. Each judge counts once.</p>
          <div className="scoring-results-table"><table><thead><tr><th>Rank</th><th>Team / participant</th>
            {criteria.map((item) => <th key={item.id}>{item.label}</th>)}<th>Overall / 5</th><th>Evaluations</th></tr></thead>
            <tbody>{results?.leaderboard.map((item,index) => <tr key={item.username}><td>{index+1}</td><td>{item.username}</td>
              {criteria.map((criterion) => <td key={criterion.id}>{Number(item.criterion_scores.find((score) => score.criterion_id === criterion.id)?.average_score).toFixed(2)}</td>)}
              <td>{Number(item.score).toFixed(2)}</td><td>{item.evaluation_count}</td></tr>)}</tbody></table></div>
          {!results?.leaderboard.length && <p>No completed rubric evaluations yet.</p>}
          <h3>Judge progress</h3><div className="scoring-results-table"><table><thead><tr><th>Judge</th><th>Completed</th><th>Allowance</th></tr></thead>
            <tbody>{results?.judges?.map((item) => <tr key={item.judge}><td>{item.judge}</td><td>{item.completed_evaluations}</td><td>{item.max_evaluations}</td></tr>)}</tbody></table></div>
        </>}
        <h3>Individual evaluations</h3>
        <input aria-label="Search evaluations" placeholder="Search team or judge" value={query} onChange={(event) => setQuery(event.target.value)} />
        <div className="scoring-results-table"><table><thead><tr><th>Judge</th><th>Team / participant</th><th>Version reviewed</th>
          {criteria.map((item) => <th key={item.id}>{item.label}</th>)}<th>Overall / {maxScore}</th><th>Feedback</th></tr></thead>
          <tbody>{evaluations.map((item) => <tr key={item.id}><td>{item.judge}</td><td>{item.participant}</td><td>{item.submission_version}</td>
            {criteria.map((criterion) => <td key={criterion.id}>{item.scores?.find((score) => score.criterion_id === criterion.id)?.score ?? '—'}</td>)}
            <td>{Number(item.overall_score).toFixed(2)}</td><td>{item.feedback || '—'}</td></tr>)}</tbody></table></div>
        {results && results.evaluations.length < (results.total_evaluations || 0) && <Button disabled={loading} onClick={() => load(true)}>Load more evaluations</Button>}
      </div>
    </div>
  </div>;
};
export default ManageCompetitionScoringResultsModal;
